import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { useLinkedProject } from '../CreatorbaseProjectSelect/hooks/useLinkedProject';
import useCreatorbaseProjectNavigationLink from '../../useCreatorbaseNavigationLink';
import CreatorbaseProject from '../CreatorbaseProject/CreatorbaseProject';
import cn from 'classnames';
import styles from './CreatorbaseProjectLink.module.css';
import { Skeleton } from '@round/ui-kit';
import CreatorbaseProjectLinkDropdown from '../CreatorbaseProjectSelect/CreatorbaseProjectLinkDropdown';
import React from 'react';
import { ReactComponent as LinkIcon } from 'assets/icons/Share.svg';
import { ReactComponent as ExclamationIcon } from 'assets/Danger.svg';
import ActionButton from '../ActionButton/ActionButton';

type Props = {
    applicationManagerProjectId: number | null | undefined;
};

const CreatorbaseProjectLink = ({ applicationManagerProjectId }: Props) => {
    const hasLinkToCreatorbaseAccess = useCheckUserGroupsAccess(['creatorbase_round_planner']);
    const isStaff = useCheckUserGroupsAccess(['user_admin']);
    const { linkedProject, isLinkedProjectLoading, hasErrorLoadingLinkedProject, ...linkedProjectData } =
        useLinkedProject(applicationManagerProjectId);

    const { creatorbaseProjectUrl, creatorbaseProject, creatorbaseTeam, creatorbaseBrand } =
        useCreatorbaseProjectNavigationLink(applicationManagerProjectId);

    const project = hasLinkToCreatorbaseAccess ? linkedProject : creatorbaseProject;
    const brand = hasLinkToCreatorbaseAccess ? linkedProjectData.brand : creatorbaseBrand;
    const team = hasLinkToCreatorbaseAccess ? linkedProjectData.team : creatorbaseTeam;

    if (!hasLinkToCreatorbaseAccess && (!isStaff || !creatorbaseProjectUrl)) {
        return null;
    }

    /*
     * Linked projects cannot be unlinked, so we display
     * the same UI for user admins and creatorbase_round_planners.
     * For creatorbase_round_planners without CB project url
     * we only display project data and no link.
     * */
    if (!hasLinkToCreatorbaseAccess || !!linkedProject) {
        return (
            <CreatorbaseProject
                project={project ?? null}
                brand={brand ?? null}
                team={team ?? null}
                projectUrl={creatorbaseProjectUrl}
            />
        );
    }

    return (
        <CreatorbaseProjectLinkDropdown projectId={applicationManagerProjectId}>
            {(targetProps) => (
                <ActionButton
                    {...targetProps}
                    type="bordered"
                    isError={hasErrorLoadingLinkedProject}
                    disabled={isLinkedProjectLoading}
                >
                    {hasErrorLoadingLinkedProject ? (
                        <ExclamationIcon className={cn(styles.actionButtonIcon, styles.error)} />
                    ) : (
                        <LinkIcon className={styles.actionButtonIcon} />
                    )}

                    {isLinkedProjectLoading ? <Skeleton width={'10rem'} /> : 'Link to Creatorbase'}
                </ActionButton>
            )}
        </CreatorbaseProjectLinkDropdown>
    );
};

export default CreatorbaseProjectLink;
